import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  ArrowLeft, Loader2, Clock, CalendarDays, XCircle, PlusCircle, FileText,
  CheckCircle2, AlertTriangle, Filter, ChevronRight, Search
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGES = {
  pending_review: { label: 'Pending Review', className: 'bg-amber-100 text-amber-800 border-amber-200' },
  auto_approved: { label: 'Auto-Approved', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  approved: { label: 'Approved', className: 'bg-green-100 text-green-800 border-green-200' },
  denied: { label: 'Denied', className: 'bg-red-100 text-red-800 border-red-200' },
  applied: { label: 'Applied', className: 'bg-blue-100 text-blue-800 border-blue-200' },
};

const TYPE_CONFIG = {
  reschedule: { label: 'Reschedule', icon: CalendarDays, color: 'text-blue-600 bg-blue-50' },
  cancel: { label: 'Cancellation', icon: XCircle, color: 'text-red-600 bg-red-50' },
  add_ons: { label: 'Add-ons Change', icon: PlusCircle, color: 'text-purple-600 bg-purple-50' },
  notes: { label: 'Note Update', icon: FileText, color: 'text-zinc-600 bg-zinc-100' },
};

const AdminChangeRequestsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/dashboard');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReq, setSelectedReq] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [actionNote, setActionNote] = useState('');
  const [actioning, setActioning] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchRequests = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('request_type', typeFilter);
      const res = await axios.get(`${API_URL}/api/change-requests?${params.toString()}`, { headers });
      setRequests(res.data);
    } catch (err) {
      toast.error('Failed to load change requests');
    } finally {
      setLoading(false);
    }
  }, [headers, statusFilter, typeFilter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleAction = async (action) => {
    if (!selectedReq) return;
    setActioning(true);
    try {
      await axios.put(
        `${API_URL}/api/change-requests/${selectedReq.id}/${action}`,
        { admin_note: actionNote || null },
        { headers }
      );
      toast.success(`Request ${action === 'approve' ? 'approved' : 'denied'} successfully`);
      setDetailOpen(false);
      setActionNote('');
      setSelectedReq(null);
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} request`);
    } finally {
      setActioning(false);
    }
  };

  const openDetail = async (req) => {
    try {
      const res = await axios.get(`${API_URL}/api/change-requests/${req.id}`, { headers });
      setSelectedReq(res.data);
      setDetailOpen(true);
      setActionNote('');
    } catch {
      toast.error('Failed to load details');
    }
  };

  const filtered = requests.filter(r => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (r.requester_name || '').toLowerCase().includes(term) ||
           r.request_type.toLowerCase().includes(term) ||
           r.id.toLowerCase().includes(term);
  });

  const pendingCount = requests.filter(r => r.status === 'pending_review').length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-change-requests-page">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center h-16 gap-4">
            <Button variant="ghost" size="icon" onClick={back} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-heading text-zinc-900">Change Requests</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {/* Filters */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-zinc-400" />
              <span className="text-sm font-medium text-zinc-600">Filters:</span>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending_review">Pending Review</SelectItem>
                <SelectItem value="auto_approved">Auto-Approved</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="denied">Denied</SelectItem>
                <SelectItem value="applied">Applied</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[160px]" data-testid="type-filter">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="reschedule">Reschedule</SelectItem>
                <SelectItem value="cancel">Cancellation</SelectItem>
                <SelectItem value="add_ons">Add-ons</SelectItem>
                <SelectItem value="notes">Notes</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <Input
                placeholder="Search by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Requests List */}
        {filtered.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <CheckCircle2 className="h-12 w-12 text-zinc-300 mx-auto mb-3" />
              <p className="text-zinc-500 text-lg">No change requests found</p>
              <p className="text-zinc-400 text-sm mt-1">Adjust your filters or check back later</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            <AnimatePresence>
              {filtered.map((req, idx) => {
                const typeConf = TYPE_CONFIG[req.request_type] || TYPE_CONFIG.notes;
                const TypeIcon = typeConf.icon;
                const statusBadge = STATUS_BADGES[req.status] || STATUS_BADGES.pending_review;
                return (
                  <motion.div
                    key={req.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.03 }}
                  >
                    <Card
                      className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => openDetail(req)}
                      data-testid={`change-request-${req.id}`}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${typeConf.color}`}>
                          <TypeIcon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-zinc-900">{typeConf.label}</span>
                            <Badge variant="outline" className={`text-xs ${statusBadge.className}`}>
                              {statusBadge.label}
                            </Badge>
                            {req.policy_fee && (
                              <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                                ${req.policy_fee} fee
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-zinc-500 truncate mt-0.5">
                            {req.requester_name || 'Unknown'} · {new Date(req.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            {req.appointment?.service_name && ` · ${req.appointment.service_name}`}
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-zinc-300 flex-shrink-0" />
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </main>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedReq && TYPE_CONFIG[selectedReq.request_type] && (
                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${TYPE_CONFIG[selectedReq.request_type].color}`}>
                  {React.createElement(TYPE_CONFIG[selectedReq.request_type].icon, { className: 'h-4 w-4' })}
                </span>
              )}
              {selectedReq && TYPE_CONFIG[selectedReq.request_type]?.label} Request
            </DialogTitle>
            <DialogDescription>
              Review the change request details and take action
            </DialogDescription>
          </DialogHeader>
          {selectedReq && (
            <div className="space-y-4 mt-2">
              {/* Status */}
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={STATUS_BADGES[selectedReq.status]?.className}>
                  {STATUS_BADGES[selectedReq.status]?.label}
                </Badge>
                {selectedReq.auto_approved && (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                    Auto
                  </Badge>
                )}
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-zinc-400">Requested by</span>
                  <p className="font-medium text-zinc-900">{selectedReq.requester_name || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-zinc-400">Date</span>
                  <p className="font-medium text-zinc-900">{new Date(selectedReq.created_at).toLocaleString()}</p>
                </div>
                {selectedReq.appointment?.start_time && (
                  <div>
                    <span className="text-zinc-400">Appointment</span>
                    <p className="font-medium text-zinc-900">{new Date(selectedReq.appointment.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                )}
                {selectedReq.appointment?.service_name && (
                  <div>
                    <span className="text-zinc-400">Service</span>
                    <p className="font-medium text-zinc-900">{selectedReq.appointment.service_name}</p>
                  </div>
                )}
              </div>

              <Separator />

              {/* Payload Details */}
              <div>
                <h4 className="text-sm font-medium text-zinc-700 mb-2">Request Details</h4>
                <div className="bg-zinc-50 rounded-lg p-3 text-sm space-y-1">
                  {selectedReq.request_type === 'reschedule' && (
                    <>
                      {selectedReq.payload_json.new_start_time && (
                        <p><span className="text-zinc-500">New date:</span> {new Date(selectedReq.payload_json.new_start_time).toLocaleString()}</p>
                      )}
                      {selectedReq.payload_json.reason && (
                        <p><span className="text-zinc-500">Reason:</span> {selectedReq.payload_json.reason}</p>
                      )}
                    </>
                  )}
                  {selectedReq.request_type === 'cancel' && (
                    <p><span className="text-zinc-500">Reason:</span> {selectedReq.payload_json.reason || 'Not provided'}</p>
                  )}
                  {selectedReq.request_type === 'add_ons' && (
                    <>
                      {selectedReq.payload_json.add_add_on_ids?.length > 0 && (
                        <p><span className="text-zinc-500">Add:</span> {selectedReq.payload_json.add_add_on_ids.length} add-on(s)</p>
                      )}
                      {selectedReq.payload_json.remove_add_on_ids?.length > 0 && (
                        <p><span className="text-zinc-500">Remove:</span> {selectedReq.payload_json.remove_add_on_ids.length} add-on(s)</p>
                      )}
                    </>
                  )}
                  {selectedReq.request_type === 'notes' && (
                    <>
                      {selectedReq.payload_json.notes_client && (
                        <p><span className="text-zinc-500">Client notes:</span> {selectedReq.payload_json.notes_client}</p>
                      )}
                      {selectedReq.payload_json.access_notes && (
                        <p><span className="text-zinc-500">Access notes:</span> {selectedReq.payload_json.access_notes}</p>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Policy Fee Warning */}
              {selectedReq.policy_fee && (
                <div className="flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-orange-800">Policy Fee: ${selectedReq.policy_fee}</p>
                    <p className="text-orange-700">This change may incur a fee per company policy.</p>
                  </div>
                </div>
              )}

              {/* Audit Trail */}
              {selectedReq.events?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-zinc-700 mb-2">Audit Trail</h4>
                  <div className="space-y-2">
                    {selectedReq.events.map((ev) => (
                      <div key={ev.id} className="flex items-start gap-2 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-zinc-300 mt-2 flex-shrink-0" />
                        <div>
                          <span className="text-zinc-900 font-medium">{ev.event_type.replace('_', ' ')}</span>
                          {ev.actor_name && <span className="text-zinc-500"> by {ev.actor_name}</span>}
                          {ev.note && <p className="text-zinc-500 text-xs mt-0.5">{ev.note}</p>}
                          <p className="text-zinc-400 text-xs">{new Date(ev.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin Actions */}
              {selectedReq.status === 'pending_review' && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <Label className="text-sm text-zinc-600">Admin Note (optional)</Label>
                    <Textarea
                      value={actionNote}
                      onChange={(e) => setActionNote(e.target.value)}
                      placeholder="Add a note for the client..."
                      className="resize-none"
                      rows={2}
                      data-testid="admin-note-input"
                    />
                    <div className="flex gap-2">
                      <Button
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => handleAction('approve')}
                        disabled={actioning}
                        data-testid="approve-btn"
                      >
                        {actioning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                        Approve & Apply
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                        onClick={() => handleAction('deny')}
                        disabled={actioning}
                        data-testid="deny-btn"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Deny
                      </Button>
                    </div>
                  </div>
                </>
              )}

              {/* Admin note display */}
              {selectedReq.admin_note && selectedReq.status !== 'pending_review' && (
                <div className="bg-zinc-50 rounded-lg p-3 text-sm">
                  <span className="text-zinc-500">Admin note:</span>
                  <p className="text-zinc-800 mt-1">{selectedReq.admin_note}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminChangeRequestsPage;
